import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { sortBy, uniqBy } from 'lodash';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { BaseMetadata } from 'services/assist-api/entities/metadata/metadatas';
import { getMetadatasByCategoryIds } from 'services/assist-api/entities/metadata/metadatas';
import type { BaseTag } from 'services/assist-api/entities/tag/tags';
import type { BaseResponseItemOption } from 'services/assist-api/entities/test-instance-response-item/types';
import { type BaseTestRubricItem } from 'services/assist-api/entities/test-rubric-item/types';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export enum QuestionType {
  Scale = 'scale',
  Multichoice = 'multichoice',
  CustomQuizEvaluation = 'custom-quiz-evaluation',
  ImageHotspot = 'image-hotspot',
  ImageCountingQuestion = 'image-counting-question',
  FillInTheBlanks = 'fill-in-the-blanks',
  Highlight = 'highlight-question',
  SortableQuestion = 'sortable-question',
  ArbLongAnswer = 'arb-long-answer',
  Unknown = 'unknown'
}

export const QUESTION_TYPE_COLLECTION_EDITABLE = [
  QuestionType.Scale,
  QuestionType.Multichoice,
  QuestionType.FillInTheBlanks,
  QuestionType.Highlight,
  QuestionType.ArbLongAnswer
];

export const QUESTION_TYPE_COLLECTION_OPTIONS = [QuestionType.Multichoice];

export const QUESTION_TYPE_COLLECTION_INTERACTIVE = [
  QuestionType.ImageHotspot,
  QuestionType.ImageCountingQuestion,
  QuestionType.FillInTheBlanks,
  QuestionType.Highlight,
  QuestionType.SortableQuestion
];

export const QUESTION_TYPE_COLLECTION_STATISTICS = [
  QuestionType.Scale,
  QuestionType.Multichoice,
  QuestionType.ImageHotspot,
  QuestionType.ImageCountingQuestion,
  QuestionType.FillInTheBlanks,
  QuestionType.Highlight,
  QuestionType.SortableQuestion
];

export type TestCategory = Category & { questionIds: number[] };

type KeyedTestCategory = { [key: string]: TestCategory };

export type TestTag = API_JSON & {
  name: string;
  parent: API_JSON;
  order: number;
};

export type Category = API_JSON & {
  name: string;
  alternativeName?: string;
  order: number;
  children?: API_JSON[];
  metadatas?: CategoryMetadata[];
};

export type BaseAnswerOption = API_JSON & BaseResponseItemOption & { correct: boolean };

type CategoryMetadata = API_JSON & {
  name: string;
  value: string;
};
export type BaseTestItem = API_JSON & {
  identifier: string;
  coreItem: boolean;
  name: string;
  description: string;
  question: string;
  stimulus: string;
  logitScore: number;
  scaleScore: number;
  compulsory: boolean;
  multipleAnswers: boolean;
  order: number;
  test: API_JSON;
  // The categories relation is missing rather than empty if the test item has no categories:
  categories?: Category[];
  type: QuestionType;
  tags: BaseTag[];
  testItemOptions: BaseAnswerOption[];
  metadatas: BaseMetadata[];
  relatedItems?: (API_JSON & { identifier: string })[];
};

export type TestItemWithRubricItems = BaseTestItem & {
  testRubricItems: BaseTestRubricItem[];
};

const order = {
  'order[order]': 'asc'
} as const;

const groups = [
  'testitem-details',
  'testitem-test',
  'testitem-testitemoptions',
  'testitem-tags',
  'tag-parent',
  'testitem-categories',
  'testitem-metadatas',
  'testitem-relateditems'
];

const include = 'tags,categories,metadatas,testItemOptions';

/**
 * Fetch test-items
 * @param params can be used to customise/limit the tests retrieved
 * @returns an array of test-items
 */
export const getTestItems: GetMany<BaseTestItem> = async (params) =>
  (
    await ASSIST_API.get('test-items', {
      params: requestParams({ order, groups, include, filters: { coreItem: 'true' } }, params)
    })
  ).data;

/**
 * Fetch test-item (singular)
 * @param params can be used to customise the test-item retrieved
 * @returns a single test-item
 */
export const getTestItem: GetOne<BaseTestItem> = async (id, params) =>
  (
    await ASSIST_API.get(`test-items/${id}`, {
      params: requestParams({ order, groups, include, filters: { coreItem: 'true' } }, params)
    })
  ).data;

export const getTestItemsFull: GetMany<BaseTestItem> = async (params) => {
  const testItems = await getTestItems(params);

  // Todo: Cannot be returned as BaseTestItem[] or GetMany<BaseTestItem>. Needs refactoring.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!testItems.meta?.totalItems) return testItems as RequestResponse<any[]>;

  // get categories metadata
  const categoryIds: number[] = uniqBy(
    testItems.json.filter((q) => q?.categories).flatMap((q) => q.categories),
    'id'
  ).map(({ id }) => id);

  const categoriesMetadata =
    categoryIds.length > 0 ? await getMetadatasByCategoryIds(categoryIds) : [];

  const newQuestions = testItems.json.map((q) => ({
    ...q,
    categories: q.categories?.map((c) => ({
      ...c,
      metadatas: categoriesMetadata?.json.filter((cm) => cm.category.id === c.id)
    })),
    relatedItems: q.relatedItems?.map((r) => ({
      ...r,
      identifier: testItems.json.find((ti: BaseTestItem) => ti.id === r.id)?.identifier
    })),
    testItemOptions: sortBy(q.testItemOptions, 'order')
  }));

  return { json: newQuestions };
};

/**
 * Hook implementation to fetch test-items
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-items matching request
 */
export const useTestItems: AssistQuery.UseMany<BaseTestItem> = (params, options) =>
  useQueryAssist(['test-items', params], () => getTestItems(params), {
    staleTime: STALE_LONG,
    ...options
  });

/**
 * Given an testItemId fetch and return the product
 *  - only runs once testItemId is provided
 *  - fetches the one test item
 *  - caches data
 * @param id testItemId
 * @param params optional parameters to change requested data
 * @returns the matching test-item
 */
export const useTestItem: AssistQuery.UseOne<BaseTestItem> = (id, params, options) =>
  useQueryAssist(['test-items', { id, ...params }], () => getTestItem(id, params), {
    staleTime: STALE_LONG,
    ...options,
    enabled: !!id
  });

export const useTestItemsByTest = (
  testId?: number,
  params?: RequestParams
): UseQueryResult<RequestResponse<BaseTestItem[]>, unknown> =>
  useQueryAssist(
    ['test-items', { ...params, filters: { test: testId } }],
    () => getTestItemsFull({ ...params, filters: { test: testId } }),
    { staleTime: STALE_LONG, enabled: !!testId }
  );

export const useTestItemsFull: AssistQuery.UseMany<BaseTestItem> = (params, options) =>
  useQueryAssist(['test-items', params], () => getTestItemsFull(params), {
    staleTime: STALE_LONG,
    ...options
  });

export const useTestCategoriesByTestId = (testId: number): TestCategory[] => {
  const testItems = useTestItemsByTest(testId).data?.json;

  const keyedCategories = useMemo(() => {
    const categories = // convert questions to category linking they belonged to
      testItems?.flatMap((q) =>
        q?.categories
          .filter((c) => !c.children || !c.children.length)
          .map((cat) => ({
            ...cat,
            questionIds: [q.id]
          }))
      );
    // convert keyed object per category
    const keyObj = categories?.reduce(
      (map, category) => ({
        ...map,
        [category.id]: {
          ...category,
          questionIds: [...(map[category.id]?.questionIds || []), category.questionIds]
        }
      }),
      {} as KeyedTestCategory
    );
    return keyObj;
  }, [testItems]);
  return Object.values(keyedCategories || {});
};
